import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import DropdownMenu, { DropdownMenuItem, DropdownMenuSeparator } from './DropdownMenu';
import ProfileSettingsModal from './ProfileSettingsModal';
import { useNotifications } from '../../pages/student-dashboard/hooks/useNotifications';
 

// Admin email - only this email has access to admin panel
const ADMIN_EMAIL = 'clubunisteps@gmail.com';
const MENU_STATE_KEY = 'unisteps-mobile-menu-open';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const notificationRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { logout, user } = useAuth();
  const { notifications, unreadCount, markAsRead, archiveNotification } = useNotifications();

  // Click outside to close notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen]);

  // Check if user is admin (only clubunisteps@gmail.com)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        // Check if user email matches the admin email
        const isAdminUser = user.email === ADMIN_EMAIL;
        setIsAdmin(isAdminUser);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Restore mobile menu state on mount to keep it open across routes
  useEffect(() => {
    try {
      const stored = localStorage.getItem(MENU_STATE_KEY);
      if (stored === 'true') setIsMobileMenuOpen(true);
    } catch (err) {
      // ignore storage errors
    }
  }, []);

  // Persist menu state so navigation does not auto-close it
  useEffect(() => {
    try {
      localStorage.setItem(MENU_STATE_KEY, isMobileMenuOpen ? 'true' : 'false');
    } catch (err) {
      // ignore storage errors
    }
  }, [isMobileMenuOpen]);

  const navigationItems = [
    { label: 'Dashboard', path: '/student-dashboard', icon: 'LayoutDashboard' },
    { label: 'Portfolio', path: '/portfolio-builder', icon: 'FolderOpen' },
    { 
      label: 'Workshops', 
      icon: 'BookOpen',
      isDropdown: true,
      dropdownItems: [
        { label: 'Discover', path: '/workshop-catalog', icon: 'Search' },
        { label: 'Enrolled', path: '/workshops-enrolled', icon: 'BookCheck' }
      ]
    },
    { 
      label: 'Universities', 
      icon: 'GraduationCap',
      isDropdown: true,
      dropdownItems: [
        { label: 'Discover Universities', path: '/universities/discover', icon: 'Search' },
        { label: 'Analyse Chances', path: '/universities/analyse', icon: 'Zap' }
      ]
    },
    { label: 'UniGuide AI', path: '/uniguide-ai', icon: 'Sparkles' },
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      const result = await logout();
      if (!result || result.success) {
        navigate('/login-register');
      } else {
        console.error('Logout error:', result.error);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Navigate anyway to clear client state
      navigate('/login-register');
    }
  };

  const handleNotificationClick = (notification) => {
    const { type, metadata } = notification;
    
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    setIsNotificationOpen(false);

    if (type === 'announcement' && metadata?.workshop_id) {
      navigate(`/workshop-announcements/${metadata.workshop_id}`);
    } else if (type === 'workshop' && metadata?.workshop_id) {
      navigate('/workshop-catalog');
    }
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notifDate = new Date(timestamp);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/65 to-background/40 backdrop-blur-xl" aria-hidden="true" />
      <div className="relative pointer-events-auto">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="glass-panel glass-stroke mt-3 sm:mt-4 flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-6 rounded-xl sm:rounded-2xl border border-border/60 shadow-lg">
            <Link to="/student-dashboard" className="flex items-center gap-2 sm:gap-3 hover:opacity-90 transition-hover min-w-[140px] sm:min-w-[180px] flex-shrink-0 mr-2 sm:mr-4 no-select">
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-br from-primary/20 via-primary/10 to-accent/10 border border-border">
                <Icon name="GraduationCap" size={18} className="sm:hidden" color="var(--color-primary)" />
                <Icon name="GraduationCap" size={22} className="hidden sm:block" color="var(--color-primary)" />
              </div>
              <div className="flex flex-col leading-tight whitespace-nowrap">
                <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.22em] text-muted-foreground">UniSteps</span>
                <span className="text-xs sm:text-sm font-semibold text-foreground">Academic Platform</span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navigationItems?.map((item, idx) => {
                if (item?.isDropdown) {
                  return (
                    <DropdownMenu
                      key={item?.label}
                      trigger={
                        <div className={`flex items-center gap-2 px-5 py-2 rounded-xl cursor-pointer lifted fade-up ${
                          item?.dropdownItems?.some(dropItem => isActivePath(dropItem?.path))
                            ? 'bg-primary text-primary-foreground shadow-md'
                            : 'text-foreground hover:bg-muted'
                        } ${`stagger-${idx % 4}`}`}>
                          <Icon name={item?.icon} size={18} />
                          <span className="font-medium">{item?.label}</span>
                          <Icon name="ChevronDown" size={14} className="ml-1" />
                        </div>
                      }
                      align="center"
                    >
                      {item?.dropdownItems?.map((dropItem) => (
                        <DropdownMenuItem 
                          key={dropItem?.path}
                          icon={dropItem?.icon}
                          onClick={() => navigate(dropItem?.path)}
                        >
                          {dropItem?.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenu>
                  );
                }
                
                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`flex items-center gap-2 px-5 py-2 rounded-xl lifted fade-up whitespace-nowrap ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'text-foreground hover:bg-muted'
                    } ${`stagger-${idx % 4}`}`}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span className="font-medium">{item?.label}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-xl hover:bg-muted transition-hover"
                aria-label="Toggle theme"
              >
                <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={20} />
              </button>
              <div className="relative" ref={notificationRef}>
                <button 
                  onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  className="relative p-2 rounded-xl hover:bg-muted transition-hover"
                  aria-label="Notifications"
                >
                  <Icon name="Bell" size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full animate-pulse"></span>
                  )}
                </button>

                {isNotificationOpen && (
                  <div className="notification-panel absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] sm:max-w-96 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-4 border-b border-border bg-muted/30">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground flex items-center gap-2">
                          <Icon name="Bell" size={18} />
                          Notifications
                        </h3>
                        {unreadCount > 0 && (
                          <span className="text-xs bg-error text-error-foreground px-2 py-0.5 rounded-full font-semibold">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="max-h-[400px] sm:max-h-[500px] overflow-y-auto mobile-scroll">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <Icon name="BellOff" size={48} className="text-muted-foreground mx-auto mb-3 opacity-50" />
                          <p className="text-sm text-muted-foreground">No notifications yet</p>
                          <p className="text-xs text-muted-foreground mt-1">We'll notify you about workshops and announcements</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-border">
                          {notifications.slice(0, 5).map((notification) => {
                            const isRead = notification?.is_read || false;
                            const isAnnouncement = notification?.type === 'announcement';

                            return (
                              <div
                                key={notification?.id}
                                onClick={() => handleNotificationClick(notification)}
                                className={`p-4 hover:bg-muted/50 active:bg-muted cursor-pointer transition-all group touch-feedback ${
                                  !isRead ? 'bg-primary/5' : ''
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg flex-shrink-0 ${
                                    isAnnouncement ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
                                  }`}>
                                    <Icon 
                                      name={isAnnouncement ? 'Megaphone' : 'BookOpen'} 
                                      size={16} 
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm line-clamp-2 ${
                                      isRead ? 'text-muted-foreground' : 'text-foreground font-medium'
                                    }`}>
                                      {notification?.message}
                                    </p>
                                    <div className="flex items-center justify-between mt-1">
                                      <span className="text-xs text-muted-foreground">
                                        {formatNotificationTime(notification?.created_at)}
                                      </span>
                                      {!isRead && (
                                        <span className="w-2 h-2 bg-primary rounded-full"></span>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      archiveNotification(notification.id);
                                    }}
                                    className="opacity-0 sm:group-hover:opacity-100 group-active:opacity-100 p-1 hover:bg-muted rounded transition-opacity touch-feedback"
                                    title="Dismiss"
                                  >
                                    <Icon name="X" size={14} className="text-muted-foreground" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-border bg-muted/30">
                        <button
                          onClick={() => {
                            setIsNotificationOpen(false);
                            navigate('/student-dashboard');
                          }}
                          className="w-full text-sm text-primary hover:text-primary/80 font-medium transition-hover flex items-center justify-center gap-1"
                        >
                          View all notifications
                          <Icon name="ArrowRight" size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DropdownMenu
                trigger={
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-muted transition-hover cursor-pointer">
                    <Icon name="User" size={20} />
                    <span className="font-medium">Account</span>
                    <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                  </div>
                }
                align="right"
              >
                <DropdownMenuItem icon="User" onClick={() => setIsSettingsOpen(true)}>
                  Profile Settings
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem icon="Shield" onClick={() => navigate('/admin')}>
                      Admin Panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  icon="LogOut" 
                  variant="destructive"
                  onClick={handleLogout}
                >
                  Log Out
                </DropdownMenuItem>
              </DropdownMenu>
            </div>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-xl hover:bg-muted transition-hover"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            className="mobile-nav-overlay fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] lg:hidden"
            onClick={toggleMobileMenu}
            style={{ pointerEvents: 'auto' }}
          ></div>
          <div className="mobile-nav-menu mobile-scroll fixed top-0 right-0 bottom-0 w-[min(85vw,360px)] bg-card border-l border-border z-[1000] lg:hidden overflow-y-auto shadow-2xl" style={{ pointerEvents: 'auto' }}>
            <div className="sticky top-0 bg-card/95 backdrop-blur-md border-b border-border p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary/20 via-primary/10 to-accent/10 border border-border">
                  <Icon name="GraduationCap" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">UniSteps</span>
                  <span className="text-sm font-semibold text-foreground">Menu</span>
                </div>
              </div>
              <button 
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg hover:bg-muted transition-hover touch-feedback no-select"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <nav className="flex flex-col p-4 gap-1 safe-bottom" style={{ pointerEvents: 'auto' }}>
              {navigationItems?.map((item, index) => {
                if (item?.isDropdown) {
                  return (
                    <div key={item?.label} className="flex flex-col gap-1 mb-2" style={{ pointerEvents: 'auto' }}>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground font-semibold text-sm uppercase tracking-wider">
                        <Icon name={item?.icon} size={18} />
                        <span>{item?.label}</span>
                      </div>
                      <div className="ml-2 flex flex-col gap-1">
                        {item?.dropdownItems?.map((dropItem) => (
                          <Link
                            key={dropItem?.path}
                            to={dropItem?.path}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all touch-feedback no-select cursor-pointer ${
                              isActivePath(dropItem?.path)
                                ? 'bg-primary text-primary-foreground shadow-md'
                                : 'text-foreground hover:bg-muted active:bg-muted'
                            }`}
                            style={{ pointerEvents: 'auto' }}
                          >
                            <Icon name={dropItem?.icon} size={20} />
                            <span className="font-medium">{dropItem?.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all touch-feedback no-select mb-1 cursor-pointer ${
                      isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'text-foreground hover:bg-muted active:bg-muted'
                    }`}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-medium">{item?.label}</span>
                  </Link>
                );
              })}

              <div className="border-t border-border my-3"></div>

              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTheme();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted active:bg-muted transition-all touch-feedback no-select cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={20} />
                <span className="font-medium">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              </button>

              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsNotificationOpen(true);
                }}
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted active:bg-muted transition-all touch-feedback no-select cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                <div className="flex items-center gap-3">
                  <Icon name="Bell" size={20} />
                  <span className="font-medium">Notifications</span>
                </div>
                {unreadCount > 0 && (
                  <span className="min-w-[20px] h-5 bg-error text-error-foreground rounded-full flex items-center justify-center text-xs font-semibold px-1.5">
                    {unreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSettingsOpen(true);
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted active:bg-muted transition-all touch-feedback no-select cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                <Icon name="User" size={20} />
                <span className="font-medium">Profile Settings</span>
              </button>

              {isAdmin && (
                <>
                  <div className="border-t border-border my-3"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/admin');
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all touch-feedback no-select cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                  >
                    <Icon name="Shield" size={20} />
                    <span className="font-medium">Admin Panel</span>
                  </button>
                </>
              )}

              <div className="border-t border-border my-3"></div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleLogout();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-error hover:bg-error/10 active:bg-error/20 transition-all touch-feedback no-select cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                <Icon name="LogOut" size={20} />
                <span className="font-medium">Log Out</span>
              </button>
            </nav>
          </div>
        </>
      )}

      <ProfileSettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />
    </header>
  );
};

export default Header;